/**
 * Reports Service - Phase 5 Implementation
 * Service layer for reports API interactions
 * Handles data fetching, caching, and transformations
 */

import { 
  ReportType,
  ReportData,
  ReportMetadata,
  ReportParams,
  ReportAPIResponse,
  ReportListAPIResponse,
  ExportConfig,
  ExportAPIResponse,
  DateRange
} from '../types/reports';
import { apiClient } from './api';
import { 
  TrendingUp, 
  IndianRupee, 
  BarChart3, 
  Scale, 
  Package, 
  Users, 
  CreditCard 
} from 'lucide-react';

class ReportsService {
  private baseUrl = '/reports';

  /**
   * Fetch report data
   */
  async fetchReport(type: ReportType, params: ReportParams): Promise<ReportAPIResponse> {
    try {
      // For demo purposes, return mock data directly
      // const response = await apiClient.post(`${this.baseUrl}/${type}/data`, params);
      
      const mockData = this.generateMockReportData(type, params);
      
      return {
        success: true,
        data: mockData,
        requestId: 'mock-request',
        executionTime: 150
      };
    } catch (error) {
      console.error('Error fetching report:', error);
      
      // Return mock data for development
      const mockData = this.generateMockReportData(type, params);
      
      return {
        success: true,
        data: mockData,
        requestId: 'mock-request',
        executionTime: 150
      };
    }
  }

  /**
   * Fetch report metadata
   */
  async fetchReportMetadata(): Promise<ReportListAPIResponse> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/metadata`);
      
      // For demo purposes, return mock metadata
      const mockMetadata = this.generateMockMetadata();
      
      return {
        success: true,
        data: mockMetadata
      };
    } catch (error) {
      console.error('Error fetching report metadata:', error);
      
      // Return mock metadata for development
      const mockMetadata = this.generateMockMetadata();
      
      return {
        success: true,
        data: mockMetadata
      };
    }
  }

  /**
   * Export report
   */
  async exportReport(type: ReportType, config: ExportConfig): Promise<ExportAPIResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${type}/export`, config);
      
      // For demo purposes, simulate export
      await this.delay(2000); // Simulate processing time
      
      const mockBlob = this.generateMockExport(type, config);
      const downloadUrl = URL.createObjectURL(mockBlob);
      
      return {
        success: true,
        downloadUrl,
        filename: config.filename || `${type}-export.${config.format}`
      };
    } catch (error) {
      console.error('Error exporting report:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Export failed'
      };
    }
  }

  /**
   * Download export (for async exports)
   */
  async downloadExport(jobId: string): Promise<ExportAPIResponse> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/exports/${jobId}/download`);
      
      return {
        success: true,
        downloadUrl: response.data.downloadUrl,
        filename: response.data.filename
      };
    } catch (error) {
      console.error('Error downloading export:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Download failed'
      };
    }
  }

  /**
   * Generate mock report data for development
   */
  private generateMockReportData(type: ReportType, params: ReportParams): ReportData {
    const metadata = this.getMockMetadataForType(type);
    
    // Generate mock chart and table data based on report type
    const { chartData, tableData } = this.generateMockDataByType(type, params.dateRange);
    
    return {
      metadata,
      chartData,
      tableData,
      summary: {
        totalRecords: tableData.length,
        dateRange: params.dateRange,
        keyMetrics: this.generateMockMetrics(type, chartData),
        trends: this.generateMockTrends(chartData),
        insights: this.generateMockInsights(type),
        lastCalculated: new Date()
      },
      lastUpdated: new Date(),
      status: 'loaded' as const
    };
  }

  /**
   * Generate mock metadata
   */
  private generateMockMetadata(): ReportMetadata[] {
    return [
      {
        id: 'sales-dashboard',
        title: 'Sales Dashboard',
        description: 'Comprehensive sales performance and revenue analytics',
        icon: TrendingUp,
        category: 'financial',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'line',
        color: 'bg-blue-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: true,
        previewValue: '₹1,24,500',
        trend: 12.5,
        lastUpdated: '2 hours ago'
      },
      {
        id: 'cash-flow-statement',
        title: 'Cash Flow Statement',
        description: 'Cash inflow and outflow analysis with balance tracking',
        icon: IndianRupee,
        category: 'financial',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'area',
        color: 'bg-green-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: true,
        previewValue: '₹45,200',
        trend: -3.2,
        lastUpdated: '1 hour ago'
      },
      {
        id: 'profit-loss',
        title: 'Profit & Loss',
        description: 'Income statement with revenue and expense breakdown',
        icon: BarChart3,
        category: 'financial',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'bar',
        color: 'bg-purple-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: true,
        previewValue: '₹89,750',
        trend: 8.7,
        lastUpdated: '3 hours ago'
      },
      {
        id: 'balance-sheet',
        title: 'Balance Sheet',
        description: 'Assets, liabilities, and equity composition analysis',
        icon: Scale,
        category: 'financial',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'composed',
        color: 'bg-indigo-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: false,
        previewValue: '₹5,67,890',
        trend: 2.1,
        lastUpdated: '4 hours ago'
      },
      {
        id: 'inventory-status',
        title: 'Inventory Status',
        description: 'Stock levels and inventory distribution by category',
        icon: Package,
        category: 'operational',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'pie',
        color: 'bg-orange-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json'],
        requiresDateRange: false,
        previewValue: '1,247 items',
        trend: -1.8,
        lastUpdated: '30 minutes ago'
      },
      {
        id: 'customer-outstanding',
        title: 'Customer Outstanding',
        description: 'Accounts receivable aging and collection analysis',
        icon: Users,
        category: 'financial',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'bar',
        color: 'bg-red-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: true,
        previewValue: '₹23,450',
        trend: 5.3,
        lastUpdated: '1 hour ago'
      },
      {
        id: 'vendor-payments',
        title: 'Vendor Payments',
        description: 'Payment trends and vendor relationship analytics',
        icon: CreditCard,
        category: 'operational',
        defaultDateRange: this.getDefaultDateRange(),
        chartType: 'line',
        color: 'bg-teal-500',
        previewEnabled: true,
        exportFormats: ['csv', 'json', 'pdf'],
        requiresDateRange: true,
        previewValue: '₹67,890',
        trend: -7.4,
        lastUpdated: '2 hours ago'
      }
    ];
  }

  /**
   * Get mock metadata for specific report type
   */
  private getMockMetadataForType(type: ReportType): ReportMetadata {
    const allMetadata = this.generateMockMetadata();
    return allMetadata.find(m => m.id === type) || allMetadata[0];
  }

  /**
   * Generate mock data by report type
   */
  private generateMockDataByType(type: ReportType, dateRange: DateRange) {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    
    switch (type) {
      case 'sales-dashboard':
        return this.generateSalesData(days);
      case 'cash-flow-statement':
        return this.generateCashFlowData(days);
      case 'profit-loss':
        return this.generateProfitLossData();
      case 'balance-sheet':
        return this.generateBalanceSheetData();
      case 'inventory-status':
        return this.generateInventoryData();
      case 'customer-outstanding':
        return this.generateCustomerOutstandingData();
      case 'vendor-payments':
        return this.generateVendorPaymentsData(days);
      default:
        return { chartData: [], tableData: [] };
    }
  }

  /**
   * Generate sales data
   */
  private generateSalesData(days: number) {
    const chartData = Array.from({ length: Math.min(days, 30) }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (30 - i));
      
      return {
        date: date.toISOString().split('T')[0],
        revenue: Math.floor(Math.random() * 50000) + 30000,
        orders: Math.floor(Math.random() * 100) + 50,
        customers: Math.floor(Math.random() * 30) + 10,
        growth: (Math.random() - 0.3) * 100
      };
    });

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      date: item.date,
      revenue: item.revenue,
      orders: item.orders,
      customers: item.customers,
      avgOrderValue: Math.round(item.revenue / item.orders),
      growth: item.growth
    }));

    return { chartData, tableData };
  }

  /**
   * Generate cash flow data
   */
  private generateCashFlowData(days: number) {
    let balance = 100000;
    
    const chartData = Array.from({ length: Math.min(days, 30) }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (30 - i));
      
      const inflow = Math.floor(Math.random() * 40000) + 60000;
      const outflow = -(Math.floor(Math.random() * 30000) + 40000);
      const netFlow = inflow + outflow;
      balance += netFlow;
      
      return {
        date: date.toISOString().split('T')[0],
        inflow,
        outflow,
        netFlow,
        balance
      };
    });

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      date: item.date,
      inflow: item.inflow,
      outflow: Math.abs(item.outflow),
      netFlow: item.netFlow,
      balance: item.balance
    }));

    return { chartData, tableData };
  }

  /**
   * Generate other mock data methods...
   */
  private generateProfitLossData() {
    const chartData = [
      { category: 'Q1 2024', revenue: 450000, expenses: 320000, netIncome: 130000, margin: 28.9 },
      { category: 'Q2 2024', revenue: 520000, expenses: 360000, netIncome: 160000, margin: 30.8 },
      { category: 'Q3 2024', revenue: 480000, expenses: 340000, netIncome: 140000, margin: 29.2 },
      { category: 'Q4 2024', revenue: 610000, expenses: 380000, netIncome: 230000, margin: 37.7 }
    ];

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    return { chartData, tableData };
  }

  private generateBalanceSheetData() {
    const chartData = [
      { account: 'Current Assets', assets: 450000, liabilities: 0, equity: 450000, ratio: 0 },
      { account: 'Fixed Assets', assets: 750000, liabilities: 0, equity: 750000, ratio: 0 },
      { account: 'Current Liabilities', assets: 0, liabilities: 180000, equity: -180000, ratio: 0.4 },
      { account: 'Long-term Debt', assets: 0, liabilities: 320000, equity: -320000, ratio: 0.71 },
      { account: 'Shareholders Equity', assets: 0, liabilities: 0, equity: 900000, ratio: 0.56 }
    ];

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    return { chartData, tableData };
  }

  private generateInventoryData() {
    const chartData = [
      { item: 'Electronics', category: 'electronics', quantity: 850, value: 425000, status: 'good', percentage: 35.2 },
      { item: 'Clothing', category: 'clothing', quantity: 1200, value: 240000, status: 'good', percentage: 19.8 },
      { item: 'Books', category: 'books', quantity: 2100, value: 157500, status: 'good', percentage: 13.0 },
      { item: 'Home & Garden', category: 'home', quantity: 640, value: 256000, status: 'low', percentage: 21.1 },
      { item: 'Sports', category: 'sports', quantity: 320, value: 132000, status: 'critical', percentage: 10.9 }
    ];

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    return { chartData, tableData };
  }

  private generateCustomerOutstandingData() {
    const chartData = [
      { customer: 'ABC Corp', current: 45000, days30: 12000, days60: 0, days90: 0, total: 57000, daysOutstanding: 25, status: 'good' },
      { customer: 'XYZ Ltd', current: 23000, days30: 18000, days60: 8000, days90: 0, total: 49000, daysOutstanding: 45, status: 'warning' },
      { customer: 'Tech Solutions', current: 0, days30: 15000, days60: 25000, days90: 12000, total: 52000, daysOutstanding: 85, status: 'overdue' }
    ];

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    return { chartData, tableData };
  }

  private generateVendorPaymentsData(days: number) {
    const chartData = Array.from({ length: Math.min(days, 14) }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (14 - i));
      
      return {
        vendor: `Vendor ${i + 1}`,
        amount: Math.floor(Math.random() * 50000) + 10000,
        paymentDate: date.toISOString().split('T')[0],
        method: ['wire', 'ach', 'check', 'auto'][Math.floor(Math.random() * 4)],
        status: ['completed', 'pending', 'scheduled'][Math.floor(Math.random() * 3)],
        daysToPayment: Math.floor(Math.random() * 45) + 15,
        onTime: Math.random() > 0.3
      };
    });

    const tableData = chartData.map((item, index) => ({
      id: index + 1,
      ...item
    }));

    return { chartData, tableData };
  }

  /**
   * Generate mock metrics
   */
  private generateMockMetrics(type: ReportType, chartData: any[]): Record<string, number | string> {
    switch (type) {
      case 'sales-dashboard':
        return {
          totalRevenue: chartData.reduce((sum, item) => sum + (item.revenue || 0), 0),
          totalOrders: chartData.reduce((sum, item) => sum + (item.orders || 0), 0),
          avgOrderValue: chartData.length > 0 ? Math.round(chartData.reduce((sum, item) => sum + (item.revenue || 0), 0) / chartData.reduce((sum, item) => sum + (item.orders || 0), 0)) : 0,
          conversionRate: '3.2%'
        };
      default:
        return {
          totalRecords: chartData.length,
          lastUpdated: new Date().toLocaleDateString()
        };
    }
  }

  /**
   * Generate mock trends
   */
  private generateMockTrends(chartData: any[]): Record<string, number> {
    return {
      revenue: (Math.random() - 0.3) * 50,
      orders: (Math.random() - 0.3) * 30,
      customers: (Math.random() - 0.3) * 40
    };
  }

  /**
   * Generate mock insights
   */
  private generateMockInsights(type: ReportType): string[] {
    const insights = {
      'sales-dashboard': [
        'Revenue increased by 15% compared to last month',
        'Customer acquisition rate is trending upward',
        'Weekend sales show strong performance'
      ],
      'cash-flow-statement': [
        'Cash flow remains positive with healthy margins',
        'Seasonal patterns detected in payment cycles',
        'Accounts receivable collection improving'
      ]
    };

    return insights[type] || ['Data analysis complete', 'No significant anomalies detected'];
  }

  /**
   * Generate mock export data
   */
  private generateMockExport(type: ReportType, config: ExportConfig): Blob {
    const data = `Mock export data for ${type} report in ${config.format} format\nGenerated: ${new Date().toISOString()}`;
    
    return new Blob([data], { 
      type: config.format === 'csv' ? 'text/csv' : 'application/json' 
    });
  }

  /**
   * Utility methods
   */
  private getDefaultDateRange(): DateRange {
    return {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      end: new Date(),
      preset: 'month'
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const reportsService = new ReportsService();